import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/client.js';

// Labels/descriptions for each flag in server/src/config/featureFlags.js —
// keep this list in sync with that allowlist. Every flag defaults OFF; the
// team should be trained on each behavior before it's turned on.
const FLAGS = [
  {
    key: 'ai_prompt_pipeline_category',
    label: 'AI asks which department a note belongs to',
    description: 'When a note looks pipeline-relevant (e.g. a quote) and the department (Parts/Service/Trailer Sales) isn’t clear, the AI will ask a clarifying question instead of leaving it unset.',
  },
  {
    key: 'notify_director_new_prospect',
    label: 'Notify the department director on a new lead/prospect',
    description: 'When a rep creates a new Lead or Prospect, the appropriate director (BDM/BDC → Jeremy + Ralph, Equipment → Ralph, Aftermarket → Pat) gets notified — same routing already used for weekly recaps.',
  },
  {
    key: 'ai_suggest_pipeline_entry',
    label: 'AI suggests adding a note to the pipeline',
    description: 'After structuring a note that looks pipeline-worthy but isn’t linked to an opportunity yet, show an inline "add to pipeline?" suggestion on the note.',
  },
];

export default function AutomationSettings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const flags = useQuery({
    queryKey: ['admin-feature-flags'],
    queryFn: () => api.get('/admin/feature-flags').then(r => r.data.data),
  });

  useEffect(() => {
    if (flags.data && !form) setForm(flags.data);
  }, [flags.data, form]);

  const save = useMutation({
    mutationFn: (feature_flags) => api.put('/admin/feature-flags', { feature_flags }).then(r => r.data.data),
    onSuccess: (data) => {
      setForm(data);
      qc.invalidateQueries({ queryKey: ['admin-feature-flags'] });
    },
  });

  if (flags.isLoading || !form) return <div className="text-muted">Loading…</div>;
  if (flags.isError) return <div className="text-crimson">Failed to load automation settings.</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <div className="section-eyebrow">Admin</div>
        <h1 className="text-2xl">Automation settings</h1>
        <div className="rule-crimson" />
        <p className="text-sm text-muted mt-1">
          New AI-driven pipeline automations, off by default. Turn each one on once the sales team
          has been trained on it — no code deploy needed.
        </p>
      </header>

      <div className="card divide-y divide-panelDark">
        {FLAGS.map(f => (
          <label key={f.key} className="flex items-start gap-3 p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!form[f.key]}
              onChange={e => setForm({ ...form, [f.key]: e.target.checked })}
            />
            <div>
              <div className="font-medium text-navy">{f.label}</div>
              <div className="text-sm text-muted">{f.description}</div>
            </div>
          </label>
        ))}
      </div>

      {save.isError && <div className="text-crimson text-sm">Failed to save.</div>}
      <button
        className="btn-primary"
        disabled={save.isPending}
        onClick={() => save.mutate(form)}
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
